import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import {
  isErpApiSuccess,
  listCustomerUsers,
  listSuppliers,
  postCashbook,
} from "../erpClient";
import { extractListRowsFromResponse } from "../utils/parseApiPagination";
import { buildJamaVoucherData } from "./JamaVoucherPDF";
import JamaVoucherPreviewModal from "./JamaVoucherPreviewModal";

function mapCustomerOption(item) {
  return {
    value: item.id,
    label: `${item.name || ""}${item.mobile ? ` (${item.mobile})` : ""}`,
    name: item.name || "",
    mobile: item.mobile || "",
    address: item.address || item.address_line || "",
    city: item.city || "",
    gst: item.gst_number || item.GST_number || item.gst || "",
  };
}

function mapSupplierOption(item) {
  const label = item.company_name || item.name || item.short_code || "";
  const partyNameForApi =
    String(item.name || "").trim() ||
    item.company_name ||
    item.short_code ||
    "";
  return {
    value: item.id,
    label,
    name: partyNameForApi,
    mobile: item.contact_number || item.mobile || "",
    address: item.address || item.address_line || "",
    city: item.city || "",
    gst: item.gst_number || item.GST_number || item.gst || "",
  };
}

function CashbookQuickPopup({
  show,
  onClose,
  cashFlowType = "cash-in",
  buttonClass = "",
  branchId = 1,
}) {
  const [activeCashTab, setActiveCashTab] = useState("customer");
  const [cashDate, setCashDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [partySearch, setPartySearch] = useState("");
  const [partyOptions, setPartyOptions] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [cashAmount, setCashAmount] = useState("");
  const [cashRemarks, setCashRemarks] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const [loadingPartyOptions, setLoadingPartyOptions] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [savingCashEntry, setSavingCashEntry] = useState(false);
  const [showBillEntries, setShowBillEntries] = useState(false);
  const [isPartyMenuOpen, setIsPartyMenuOpen] = useState(false);
  const [showVoucherPreview, setShowVoucherPreview] = useState(false);
  const [voucherData, setVoucherData] = useState(null);

  const txType = cashFlowType === "cash-out" ? "CASH_OUT" : "CASH_IN";
  const isCashIn = cashFlowType !== "cash-out";

  const getPartyTypeCode = () =>
    activeCashTab === "customer" ? "CUSTOMER" : "SUPPLIER";

  useEffect(() => {
    if (!show) return;
    setActiveCashTab(cashFlowType === "cash-out" ? "supplier" : "customer");
    setCashDate(new Date().toISOString().split("T")[0]);
    setPartySearch("");
    setPartyOptions([]);
    setSelectedParty(null);
    setCashAmount("");
    setCashRemarks("");
    setSummaryData(null);
    setShowBillEntries(false);
    setIsPartyMenuOpen(false);
  }, [show, cashFlowType]);

  useEffect(() => {
    if (!show) return;
    setSelectedParty(null);
    setPartySearch("");
    setPartyOptions([]);
    setSummaryData(null);
    setShowBillEntries(false);
    setIsPartyMenuOpen(false);
  }, [activeCashTab, show]);

  useEffect(() => {
    if (!show) return;
    const keyword = String(partySearch || "").trim();
    if (!keyword) {
      setPartyOptions([]);
      setIsPartyMenuOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingPartyOptions(true);
      try {
        if (activeCashTab === "customer") {
          const response = await listCustomerUsers({ search: keyword });
          const rows = extractListRowsFromResponse(response, ["customers"]);
          setPartyOptions(rows.map(mapCustomerOption));
          setIsPartyMenuOpen(true);
        } else {
          const response = await listSuppliers({ search: keyword, status: 1 });
          const rows = extractListRowsFromResponse(response, ["suppliers"]);
          setPartyOptions(
            rows
              .filter((item) =>
                String(item.company_name || item.name || "")
                  .toLowerCase()
                  .includes(keyword.toLowerCase()),
              )
              .map(mapSupplierOption),
          );
          setIsPartyMenuOpen(true);
        }
      } catch {
        setPartyOptions([]);
        setIsPartyMenuOpen(false);
      } finally {
        setLoadingPartyOptions(false);
      }
    }, 220);

    return () => clearTimeout(timer);
  }, [partySearch, activeCashTab, show]);

  const fetchPartyDueSummary = async (party) => {
    if (!party?.value) return;
    setLoadingSummary(true);
    try {
      const response = await postCashbook({
        type: "party_due_summary",
        party_type: getPartyTypeCode(),
        party_id: party.value,
        party_name: party.name || "",
        branch_id: branchId,
      });
      if (isErpApiSuccess(response) || response?.status === true) {
        const data = response?.data || null;
        setSummaryData(data);
        setCashAmount(
          data?.total_due_amount != null ? String(data.total_due_amount) : "",
        );
      } else {
        setSummaryData(null);
        toast.error(response?.message || "Unable to load due summary");
      }
    } catch (error) {
      setSummaryData(null);
      toast.error(error?.message || "Unable to load due summary");
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleSaveCashSettlement = async ({ print = false } = {}) => {
    if (!selectedParty || !cashAmount || Number(cashAmount) <= 0) return;
    const idempotencyKey = `cash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const amountToSave = Number(cashAmount);
    const remarksToSave = cashRemarks || "";
    const dateToSave = cashDate;
    const partySnapshot = { ...selectedParty };

    setSavingCashEntry(true);
    try {
      const response = await postCashbook(
        {
          type: "cash_settlement",
          branch_id: branchId,
          idempotency_key: idempotencyKey,
          tx_type: txType,
          party_type: getPartyTypeCode(),
          party_id: selectedParty.value,
          party_name: selectedParty.name || "",
          amount: amountToSave,
          date: dateToSave,
          remarks: remarksToSave,
        },
        { idempotencyKey },
      );
      if (isErpApiSuccess(response) || response?.status === true) {
        toast.success("Cash settlement saved successfully");

        if (print) {
          setVoucherData(
            buildJamaVoucherData({
              isIn: isCashIn,
              party: partySnapshot,
              amount: amountToSave,
              balance: summaryData?.total_due_amount,
              date: dateToSave,
              remarks: remarksToSave,
              paymentModeLabel: "Cash",
              apiResponse: response,
            }),
          );
          setShowVoucherPreview(true);
          onClose?.();
        } else {
          setCashAmount("");
          setCashRemarks("");
          await fetchPartyDueSummary(selectedParty);
        }
      } else {
        toast.error(response?.message || "Failed to save cash settlement");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save cash settlement");
    } finally {
      setSavingCashEntry(false);
    }
  };

  const canSave = useMemo(
    () => selectedParty && cashAmount !== "" && Number(cashAmount) > 0,
    [selectedParty, cashAmount],
  );

  const partySelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: 44,
      height: 44,
      borderRadius: 10,
      borderColor: "#dce3ea",
      boxShadow: "none",
      "&:hover": { borderColor: "#c7d0db" },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: 44,
      padding: "0 10px",
    }),
    input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
    menu: (provided) => ({ ...provided, zIndex: 2000, borderRadius: 10 }),
    menuList: (provided) => ({ ...provided, maxHeight: 240 }),
    option: (base, state) => {
      const active = Boolean(state.isFocused || state.isSelected);
      return {
        ...base,
        backgroundColor: active
          ? "var(--theme-primary, #000000)"
          : "#ffffff",
        color: active ? "#ffffff" : "var(--theme-text-primary, #212529)",
        cursor: "pointer",
        ":active": {
          ...base[":active"],
          backgroundColor: "var(--theme-primary, #000000)",
          color: "#ffffff",
        },
      };
    },
  };

  if (!show && !showVoucherPreview) return null;

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            {cashFlowType === "cash-out" ? "Cash-OUT" : "Cash-IN"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="d-flex gap-2 mb-3">
            <button
              type="button"
              className={`btn flex-fill ${activeCashTab === "customer" ? "btn-primary" : "btn-light border"} ${buttonClass}`}
              onClick={() => setActiveCashTab("customer")}
            >
              Customer
            </button>
            <button
              type="button"
              className={`btn flex-fill ${activeCashTab === "supplier" ? "btn-primary" : "btn-light border"} ${buttonClass}`}
              onClick={() => setActiveCashTab("supplier")}
            >
              Supplier/Karigar
            </button>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <label className="form-label fw-semibold mb-0">Party Name <span className="text-danger">*</span></label>
                {selectedParty && (
                  <span
                    className="badge bg-danger-subtle text-danger"
                    style={{ fontWeight: 700 }}
                  >
                    Due:{" "}
                    {loadingSummary
                      ? "..."
                      : (summaryData?.total_due_amount ?? 0)}
                  </span>
                )}
              </div>
              <Select
                className="w-100"
                classNamePrefix="party-select"
                styles={partySelectStyles}
                components={{
                  DropdownIndicator: () => null,
                  ClearIndicator: () => null,
                }}
                options={partyOptions}
                value={selectedParty}
                inputValue={partySearch}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === "input-change")
                    setPartySearch(value);
                }}
                onChange={(option) => {
                  setSelectedParty(option);
                  setPartySearch("");
                  setShowBillEntries(false);
                  setIsPartyMenuOpen(false);
                  if (option) fetchPartyDueSummary(option);
                  else setSummaryData(null);
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === "ArrowDown" &&
                    partyOptions.length > 0 &&
                    !isPartyMenuOpen
                  ) {
                    event.preventDefault();
                    setIsPartyMenuOpen(true);
                  }
                }}
                menuIsOpen={isPartyMenuOpen}
                onMenuOpen={() => setIsPartyMenuOpen(true)}
                onMenuClose={() => setIsPartyMenuOpen(false)}
                isLoading={loadingPartyOptions}
                openMenuOnFocus
                placeholder={
                  activeCashTab === "customer"
                    ? "Enter customer name or mobile"
                    : "Enter supplier/karigar name"
                }
                noOptionsMessage={() => "No matching records"}
                isClearable
              />
            </div>
          </div>

          <div className="row g-3 align-items-stretch">
            <div className="col-12">
              <div className="rounded-3 border p-3 h-100">
                <h6 className="fw-semibold mb-3">Cash Entry</h6>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Amount <span className="text-danger">*</span></label>
                  <input
                    type="number"
                    min="0"
                    className={`form-control ${buttonClass}`}
                    placeholder="Enter amount"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={cashDate}
                    onChange={(e) => setCashDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1">Remarks</label>
                  <textarea
                    rows={3}
                    className={`form-control ${buttonClass}`}
                    placeholder="Enter remarks"
                    value={cashRemarks}
                    onChange={(e) => setCashRemarks(e.target.value)}
                  />
                </div>
                <div className="d-flex justify-content-end gap-2 pt-1 flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-primary ${buttonClass}`}
                    onClick={() => handleSaveCashSettlement({ print: false })}
                    disabled={savingCashEntry || !canSave}
                  >
                    {savingCashEntry ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${buttonClass}`}
                    onClick={() => handleSaveCashSettlement({ print: true })}
                    disabled={savingCashEntry || !canSave}
                  >
                    {savingCashEntry ? "Saving..." : "Save & Print"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <JamaVoucherPreviewModal
        show={showVoucherPreview}
        onHide={() => {
          setShowVoucherPreview(false);
          setVoucherData(null);
        }}
        voucherData={voucherData}
      />
    </>
  );
}

export default CashbookQuickPopup;
